import { zodResolver } from "@hookform/resolvers/zod";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Fragment, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { Button, ErrorMessage, FileInput, Icon, PhoneNumberInput, TextArea, TextInput, Typography } from "@/components/atoms";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import {
  useGetClientBookingDocumentsQuery,
  useLazyGetClientBookingDocumentQuery,
} from "@/redux/apis/clientBooking/clientBookingApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setAdditionalInfo, setBookedBy, setPassengerDetails, setReference } from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectAdditionalInfo,
  selectBookedBy,
  selectEditJobId,
  selectNumOfPassengers,
  selectPassengerDetails,
  selectReference,
} from "@/redux/slices/clientBooking/selectors";
import { addToast, clsx } from "@/utils";
import { useBookingAttachments } from "./BookingAttachmentsContext";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";
import { useForceValidate } from "./useForceValidate";
import { useBookedByName } from "./useBookedByPrefill";
import { useMainPassenger } from "./useMainPassenger";

type Layout = "card" | "sheet";

interface OtherDetailsPanelProps {
  /** "card" = desktop side panel; "sheet" = mobile bottom sheet (adds a Close action). */
  layout: Layout;
  /** Called after committing — advances the flow (this is the last sub-step, so it moves on to Payment). */
  onProceed: () => void;
  onClose: () => void;
  /** When true, show field errors immediately (the user was routed here by a failed exit). */
  forceValidate?: boolean;
}

// Validation mirrors the old sheet's Laminas form elements (web-app BookingManager/Form/Element):
//   Booked by       — required, ≤100 (BookedBy.php). Pre-seeded, so it's only empty if cleared.
//   Reference       — optional, ≤100 (ReferenceNumber.php).
//   Additional info — optional, ≤255 (AdditionalInformation.php).
// Passenger #1 is locked (seeded from "Who is this booking for?") so it never validates here; the
// remaining passenger rows are optional — a name is free text (≤255, Name.php) and a phone is only
// checked for format when entered (the old backend did no phone-format validation; ours is stricter).
const otherDetailsSchema = z.object({
  bookedBy: z.string().min(1, "Booked by is required").max(100, "Booked by must be 100 characters or less"),
  reference: z.string().max(100, "Reference number must be 100 characters or less"),
  additionalInfo: z.string().max(255, "Notes must be 255 characters or less"),
  passengers: z.array(
    z.object({
      name: z.string().max(255, "Name must be 255 characters or less"),
      phone: z.string().refine((value) => !value || isValidPhoneNumber(value), "Invalid phone number"),
    })
  ),
});
// Explicit type (not z.infer) so react-hook-form's FieldPath still recurses into the passengers
// array — the phone field's .refine() wraps it in a ZodEffects that otherwise breaks path inference.
type OtherDetailsForm = {
  bookedBy: string;
  reference: string;
  additionalInfo: string;
  passengers: { name: string; phone: string }[];
};

// Attachment limits, aligned to the client document-upload endpoint (Journey BookingDocument →
// FileUpload extends FileImage: 20MB cap). Documents (doc/docx) are intentionally excluded even
// though the backend accepts them — a booking attachment is an image or PDF (e.g. a company logo).
// The backend also accepts these, so anything that passes here won't be rejected server-side.
const ATTACHMENT_ACCEPT = "image/png,image/jpeg,image/gif,application/pdf";
const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024; // 20 MB
const ATTACHMENT_ALLOWED_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "pdf"];

// The backend masks driver-facing contact details for the primary passenger plus 10 others
// (JobMessagingProxyService::MAX_MASKED_PASSENGERS in web-app). Anyone past that has their real
// phone number shared with the driver, so the booker is told before they enter it.
const MAX_MASKED_PASSENGERS = 11;

/** An error message if the picked file isn't an allowed image/PDF within the size cap, else null. */
const validateAttachment = (file: File): string | null => {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ATTACHMENT_ALLOWED_EXTENSIONS.includes(extension)) return "Unsupported file type. Please use PNG, JPG, GIF or PDF.";
  if (file.size > ATTACHMENT_MAX_BYTES) return "File is too large. The maximum size is 20 MB.";
  return null;
};

/** A form section: label above its control. Mirrors the Field helper in VehicleTransferPanel. */
const Field = ({ label, children }: React.PropsWithChildren<{ label: string }>) => (
  <div className="flex flex-col gap-2">
    <Typography variant="action" className="block text-neutral-dark-gray">
      {label}
    </Typography>
    {children}
  </div>
);

/**
 * "Other Details" — the fourth sub-panel of the Trip Details step. Captures Booked by, Reference
 * Number, additional notes to the driver, and a per-passenger detail list. The number of passenger
 * rows follows the Vehicle & Transfer step's passenger count; row 1 is seeded from the passenger
 * selected in "Who is this booking for?". Committed to clientBookingSlice on Proceed (not live),
 * matching the sibling panels.
 */
export const OtherDetailsPanel = ({ layout, onProceed, onClose, forceValidate = false }: OtherDetailsPanelProps) => {
  const dispatch = useAppDispatch();
  const viewOnly = useBookingViewOnly();
  // Edit mode relabels the commit CTA "Confirm" (it closes the sub-panel rather than advancing).
  const isEdit = useAppSelector(selectEditJobId) != null;
  const numOfPassengers = useAppSelector(selectNumOfPassengers);
  const bookedBy = useAppSelector(selectBookedBy);
  const reference = useAppSelector(selectReference);
  const additionalInfo = useAppSelector(selectAdditionalInfo);
  const storedPassengers = useAppSelector(selectPassengerDetails);
  // Files live in the sheet-scoped context (File isn't redux-serializable); uploaded after Book Trip.
  const { files, addFiles, removeFile, removedExistingIds, toggleRemoveExisting, clearRemovedExisting } = useBookingAttachments();

  // View/edit of an existing booking (route carries :jobId) → show the attachments already on the job.
  // The create flow has no :jobId, so this skips. `accountId` scopes the fetch under operator OBO.
  const { jobId } = useParams<{ jobId?: string }>();
  const { accountId } = useClientScope();
  const { data: existingDocs = [] } = useGetClientBookingDocumentsQuery(
    jobId ? { jobId: Number(jobId), accountId: accountId ?? undefined } : skipToken
  );
  // Only one attachment is allowed, so the upload button appears only when the single slot is free —
  // i.e. there was never an attachment, or the existing one has been staged for removal.
  const canAddAttachment = existingDocs.every((doc) => removedExistingIds.includes(doc.uuid));
  // The list omits the presigned link, so fetch the single document on demand to open it.
  const [fetchDocument] = useLazyGetClientBookingDocumentQuery();
  const handleDownload = async (uuid: string) => {
    if (!jobId) return;
    try {
      const doc = await fetchDocument({ jobId: Number(jobId), uuid, accountId: accountId ?? undefined }, true).unwrap();
      if (doc.link) window.open(doc.link, "_blank", "noopener,noreferrer");
      else addToast("info", "This attachment isn't available to download.");
    } catch {
      addToast("danger", "Couldn't open the attachment. Please try again.");
    }
  };

  const count = numOfPassengers ?? 1;
  // Passenger #1 is the passenger from "Who is this booking for?" — the chosen guest, the account
  // holder, or (guest flow) the guest's own contact. Locked/prepopulated, derived live via the shared
  // hook so guest and authed both resolve a real name (never "undefined undefined"), never edited here.
  const { name: seedName, phone: seedPhone } = useMainPassenger();
  // "Booked by" is the person OPERATING the sheet, not the passenger — a different question from
  // seedName above, and the same source useBookedByPrefill seeds the draft from. That hook normally
  // wins (it runs on entering Trip Details, before this panel mounts); this covers the first render
  // if it hasn't landed yet, so the two can't disagree.
  const bookedByName = useBookedByName();
  // The editable rows are passengers #2..N. A stored row wins over blank so edits survive reopening.
  const extraCount = Math.max(0, count - 1);
  const extraPassengers = Array.from({ length: extraCount }, (_, i) => storedPassengers?.[i + 1] ?? { name: "", phone: "" });

  const {
    control,
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<OtherDetailsForm>({
    resolver: zodResolver(otherDetailsSchema),
    defaultValues: {
      bookedBy: bookedBy ?? bookedByName,
      reference: reference ?? "",
      additionalInfo: additionalInfo ?? "",
      passengers: extraPassengers,
    },
  });

  // Routed here by a failed "Next Step": surface the field errors (e.g. a cleared "Booked by") now.
  useForceValidate(forceValidate, () => void trigger());

  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const handleProceed = (form: OtherDetailsForm) => {
    dispatch(setBookedBy(form.bookedBy.trim()));
    dispatch(setReference(form.reference.trim()));
    dispatch(setAdditionalInfo(form.additionalInfo.trim()));
    // Passenger #1 committed from the (locked) selection; the rest from the editable rows.
    dispatch(
      setPassengerDetails([
        { name: seedName.trim(), phone: seedPhone },
        ...form.passengers.map((p) => ({ name: p.name.trim(), phone: p.phone })),
      ])
    );
    onProceed();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ViewOnlyFieldset>
      <form className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5" onSubmit={handleSubmit(handleProceed)}>
        <Typography variant="h2">Other Details</Typography>
        <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
          Passenger, Notes, and Others
        </Typography>

        <div className="mt-6 flex flex-col gap-6">
          <Field label="Booked by">
            <TextInput size="md" placeholder="Enter who booked this trip" hasError={!!errors.bookedBy} {...register("bookedBy")} />
            <ErrorMessage errors={errors} name="bookedBy" />
          </Field>

          <Field label="Reference Number">
            <TextInput size="md" placeholder="Enter a reference number" hasError={!!errors.reference} {...register("reference")} />
            <ErrorMessage errors={errors} name="reference" />
          </Field>

          <Field label="Additional notes to your driver">
            <TextArea
              placeholder="Enter additional notes to your driver"
              hasError={!!errors.additionalInfo}
              {...register("additionalInfo")}
            />
            <ErrorMessage errors={errors} name="additionalInfo" />
          </Field>

          {/* Single attachment for now (the document endpoint takes one file per request). The
              upload button shows until a file is chosen; remove it to swap. */}
          <Field label="Attachments">
            {/* Existing (already-uploaded) attachments — shown when viewing/editing a booking. Tapping the
                name downloads it; the X stages removal (applied only on "Update Booking", never on tap). */}
            {existingDocs.map((doc) => {
              const staged = removedExistingIds.includes(doc.uuid);
              // Single slot: once a replacement file is picked, hide the staged-for-removal row so there's
              // no Undo path that would leave two attachments. Removing the new file brings it back.
              if (staged && files.length > 0) return null;
              return (
                <div
                  key={doc.uuid}
                  className="flex items-center gap-2 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2"
                >
                  <Icon name="DocumentText" size="md" className="shrink-0 text-neutral-dark-gray" />
                  <button
                    type="button"
                    onClick={() => void handleDownload(doc.uuid)}
                    className="min-w-0 flex-1 truncate text-left"
                  >
                    <Typography
                      variant="paragraph"
                      className={clsx("truncate text-neutral-dark-gray hover:underline", staged && "text-neutral-silver-gray line-through")}
                    >
                      {doc.filename}
                    </Typography>
                  </button>
                  {!viewOnly &&
                    (staged ? (
                      <button
                        type="button"
                        aria-label={`Undo remove ${doc.filename}`}
                        onClick={() => toggleRemoveExisting(doc.uuid)}
                        className="shrink-0 text-info"
                      >
                        <Typography variant="small-regular" className="text-info">
                          Undo
                        </Typography>
                      </button>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Remove ${doc.filename}`}
                        onClick={() => toggleRemoveExisting(doc.uuid)}
                        className="shrink-0 text-neutral-dark-gray hover:text-danger"
                      >
                        <Icon name="Add" size="md" className="rotate-45" />
                      </button>
                    ))}
                </div>
              );
            })}
            {/* A newly-picked file shows as the chosen row. Otherwise the upload button shows only when
                the single attachment slot is free (no active existing attachment). */}
            {files.length > 0 ? (
              <div className="flex items-center gap-2 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
                <Icon name="DocumentText" size="md" className="shrink-0 text-neutral-dark-gray" />
                <Typography variant="paragraph" className="min-w-0 flex-1 truncate text-neutral-dark-gray">
                  {files[0].name}
                </Typography>
                <button
                  type="button"
                  aria-label={`Remove ${files[0].name}`}
                  // Cancelling the replacement reverts to the original attachment as a normal active row
                  // (not the struck-through/Undo state) — so it's unmistakably the original, not this file.
                  onClick={() => {
                    removeFile(0);
                    clearRemovedExisting();
                  }}
                  className="shrink-0 text-neutral-dark-gray hover:text-danger"
                >
                  <Icon name="Add" size="md" className="rotate-45" />
                </button>
              </div>
            ) : canAddAttachment ? (
              <>
                <FileInput
                  fullWidth
                  label="Upload file or company logo"
                  accept={ATTACHMENT_ACCEPT}
                  onChange={(e) => {
                    const picked = e.target.files?.[0];
                    e.target.value = "";
                    if (!picked) return;
                    const error = validateAttachment(picked);
                    if (error) {
                      setAttachmentError(error);
                      return;
                    }
                    setAttachmentError(null);
                    addFiles([picked]);
                  }}
                />
                {attachmentError ? (
                  <Typography variant="paragraph" className="block text-danger">
                    {attachmentError}
                  </Typography>
                ) : (
                  <Typography variant="small-regular" className="block text-neutral-silver-gray">
                    PNG, JPG, GIF or PDF · up to 20 MB
                  </Typography>
                )}
              </>
            ) : null}
          </Field>
        </div>

        {/* Full-bleed divider between the trip fields and the passenger list (20px above/below). */}
        <div className="-mx-5 my-5 h-px shrink-0 bg-neutral-mid-gray" />

        <Typography variant="title" className="block">
          Passenger Details
        </Typography>

        <div className="mt-4 flex flex-col gap-6">
          {/* Primary passenger — locked, prepopulated from "Who is this booking for?". */}
          <div className="flex flex-col gap-2">
            <Typography variant="action" className="block text-neutral-dark-gray">
              Primary Passenger #1
            </Typography>
            <TextInput size="md" value={seedName} disabled readOnly />
            <TextInput size="md" value={seedPhone} disabled readOnly />
          </div>

          {/* Passengers #2..N — optional name + phone. */}
          {extraPassengers.map((_, i) => (
            <Fragment key={i}>
              {/* Row index 10 is "Passenger #12" — the first passenger the backend can't mask. */}
              {i === MAX_MASKED_PASSENGERS - 1 && (
                <Typography variant="small-regular" className="block text-neutral-silver-gray">
                  Contact masking covers the first 11 passengers — phone numbers for passengers beyond 11 are shared directly with
                  your driver.
                </Typography>
              )}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <Typography variant="action" className="text-neutral-dark-gray">
                    Passenger #{i + 2}
                  </Typography>
                  <Typography variant="action" className="text-neutral-silver-gray">
                    Optional
                  </Typography>
                </div>
                <TextInput size="md" placeholder="Enter Name" {...register(`passengers.${i}.name`)} />
                <PhoneNumberInput control={control} name={`passengers.${i}.phone`} size="md" hasError={!!errors.passengers?.[i]?.phone} />
                {errors.passengers?.[i] && <ErrorMessage errors={errors.passengers[i] as FieldErrors} name="phone" />}
              </div>
            </Fragment>
          ))}
        </div>
      </form>
      </ViewOnlyFieldset>

      {/* Pinned footer: 12px above the buttons with a divider (matches the sibling panels when the body scrolls). */}
      {!viewOnly && (
        <div className="flex flex-col gap-2 border-t border-neutral-mid-gray px-5 pb-5 pt-3">
          <Button variant="primary" size="lg" className="w-full" onClick={handleSubmit(handleProceed)}>
            {isEdit ? "Confirm" : "Proceed"}
          </Button>
          {layout === "sheet" && (
            <Button variant="custom" size="md" className="text-info" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
